import {
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";

import Link from "../../../atoms/mui/Link";

import { analyticsTrack } from "../../../../helpers/analytics";

import usePermissions from "../../../../helpers/hooks/usePermissions";

import { useScoutCardContext } from "../ScoutCardContext";
import { ACTION_HANDLERS, SHARE_ACTION_TYPES } from "./actionHandlers";
import { usePdfReportDownload } from "../../reports/usePdfReportDownload";

const CardBody = ({ label, description, palette, icon }) => (
  <Box
    p={2}
    style={{
      backgroundColor: palette.backgroundGray.light,
      border: `1px solid ${palette.primary.main}`,
      borderRadius: "8px",
      width: "100%"
    }}
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
  >
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="subtitle2" color="primary" align="left">
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="textSecondary" align="left">
          {description}
        </Typography>
      )}
    </Box>
    {icon}
  </Box>
);

const ArrowIcon = ({ palette }) => (
  <FontAwesomeIcon
    icon={faArrowRight}
    color={palette.primary.main}
    style={{ fontSize: 16 }}
  />
);

const PdfReportCard = ({ label, description, payload }) => {
  const { palette } = useTheme();
  const { trigger, isGenerating, error, rendererJsx } = usePdfReportDownload({
    triggerPath: "scout_card"
  });

  const onClick = () => {
    if (isGenerating) {
      return;
    }
    analyticsTrack("scout_action_card_clicked", {
      actionType: "download_pdf_report",
      anchor: payload?.anchor || null,
      timestamp: Date.now()
    });
    trigger({ year: payload?.year });
  };

  const idleIcon = <ArrowIcon palette={palette} />;
  const generatingIcon = <CircularProgress size={16} color="primary" />;

  const displayLabel = isGenerating
    ? `Preparing your ${payload?.year || ""} report...`
    : error
      ? "Try again — couldn't generate"
      : label;
  const displayDescription = isGenerating || error ? null : description;
  const displayIcon = isGenerating ? generatingIcon : idleIcon;

  return (
    <>
      <ButtonBase
        onClick={onClick}
        disabled={isGenerating}
        style={{ width: "100%", textAlign: "left" }}
      >
        <CardBody
          label={displayLabel}
          description={displayDescription}
          palette={palette}
          icon={displayIcon}
        />
      </ButtonBase>
      {rendererJsx}
    </>
  );
};

const ScoutActionCard = ({ label, actionType, description, payload }) => {
  const { palette } = useTheme();
  const { pathname } = useLocation();
  const {
    openSupport,
    openExpertMeeting,
    shareCustomerYear,
    acceptSupplierShare
  } = useScoutCardContext();
  const { canShare } = usePermissions();
  const handler = ACTION_HANDLERS[actionType];

  const isBlockedShareAction =
    SHARE_ACTION_TYPES.has(actionType) && !canShare;

  const trackClick = () =>
    analyticsTrack("scout_action_card_clicked", {
      actionType,
      anchor: payload?.anchor || null,
      timestamp: Date.now()
    });

  if (!handler) return null;

  if (isBlockedShareAction) return null;

  if (handler.type === "pdf_report") {
    return (
      <PdfReportCard
        label={label}
        description={description}
        payload={payload}
      />
    );
  }

  const deps = {
    openSupport,
    openExpertMeeting,
    shareCustomerYear,
    acceptSupplierShare
  };

  if (handler.type === "link") {
    const href = handler.hrefBuilder(payload || {});
    return (
      <Link href={href} onClick={trackClick}>
        <CardBody
          label={label}
          description={description}
          palette={palette}
          icon={<ArrowIcon palette={palette} />}
        />
      </Link>
    );
  }

  if (handler.type === "pageAware") {
    const resolved = handler.resolve(deps, payload || {}, { pathname });
    if (!resolved) return null;
    if (resolved.type === "link") {
      return (
        <Link href={resolved.href} onClick={trackClick}>
          <CardBody
            label={label}
            description={description}
            palette={palette}
            icon={<ArrowIcon palette={palette} />}
          />
        </Link>
      );
    }
    const fn = resolved.fn;
    const onPageAwareClick = () => {
      trackClick();
      if (typeof fn === "function") fn();
    };
    return (
      <ButtonBase
        onClick={onPageAwareClick}
        style={{ width: "100%", textAlign: "left" }}
      >
        <CardBody
          label={label}
          description={description}
          palette={palette}
          icon={<ArrowIcon palette={palette} />}
        />
      </ButtonBase>
    );
  }

  const callback = handler.resolve(deps, payload || {});
  const onClick = () => {
    trackClick();
    if (typeof callback === "function") callback();
  };

  return (
    <ButtonBase onClick={onClick} style={{ width: "100%", textAlign: "left" }}>
      <CardBody
        label={label}
        description={description}
        palette={palette}
        icon={<ArrowIcon palette={palette} />}
      />
    </ButtonBase>
  );
};

export default ScoutActionCard;
